const MAX_PX = 2400;
const JPEG_QUALITY = 0.82;
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png'];

// Decode a data URL into an ImageBitmap (with EXIF orientation applied) or
// HTMLImageElement as fallback. createImageBitmap with imageOrientation:
// 'from-image' is the reliable cross-browser path for EXIF auto-rotation.
interface ImageFile {
  path: string;
  extension?: string;
  [key: string]: unknown;
}

async function decodeWithOrientation(
  dataUrl: string,
): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap !== 'undefined') {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      return await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      // fall through to Image fallback
    }
  }
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function resizeImageFile(file: ImageFile): Promise<ImageFile> {
  const { path: dataUrl, extension } = file;
  if (!PHOTO_EXTENSIONS.includes((extension || '').toLowerCase())) return file;

  let source;
  try {
    source = await decodeWithOrientation(dataUrl);
  } catch {
    return file;
  }

  // ImageBitmap uses .width/.height; HTMLImageElement uses .naturalWidth/.naturalHeight
  const w = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const h = 'naturalHeight' in source ? source.naturalHeight : source.height;

  if (w <= MAX_PX && h <= MAX_PX) {
    if ('close' in source) source.close();
    return file;
  }

  const scale = Math.min(MAX_PX / w, MAX_PX / h);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  canvas.getContext('2d')!.drawImage(source, 0, 0, canvas.width, canvas.height);
  if ('close' in source) source.close();

  return {
    ...file,
    extension: 'jpg',
    path: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
  };
}
